import { createTheme } from '@mui/material/styles';

// Create a respectful black and white theme for memorial pages
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#212121', // Very dark gray (almost black)
      light: '#484848',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#424242', // Dark gray
      light: '#6d6d6d',
      dark: '#1b1b1b',
      contrastText: '#ffffff',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    background: {
      default: '#f5f5f5', // Light gray background
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
    error: {
      main: '#b71c1c', // Darker red that fits with theme
    },
    warning: {
      main: '#d32f2f', // Darker amber for warnings
    },
    info: {
      main: '#424242', // Dark gray for info
    },
    success: {
      main: '#2e7d32', // Keep green for success messages
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    subtitle2: {
      fontWeight: 500,
    },
    body1: {
      lineHeight: 1.7,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none', // More modern look without all caps
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#212121', // Very dark gray (almost black)
          color: '#ffffff',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          boxShadow: 'none',
          transition: 'all 0.25s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#484848', // Slightly lighter than primary.main
          },
        },
        outlinedPrimary: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
        // Special styling for checkout buttons
        containedSecondary: {
          backgroundColor: '#424242', // Dark gray for secondary buttons
          '&:hover': {
            backgroundColor: '#616161',
          },
        },
      },
      variants: [
        {
          props: { variant: 'purchase' },
          style: {
            backgroundColor: '#212121', // Dark for purchase buttons
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#424242',
              boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)',
            },
          },
        },
        {
          props: { variant: 'memorial' },
          style: {
            backgroundColor: '#424242', // Keep gray for memorial-related buttons
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#616161',
            },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0 3px 15px rgba(0, 0, 0, 0.07)',
        },
        elevation3: {
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: '#e8f5e9', // Lighter green
          color: '#1b5e20',
        },
        standardInfo: {
          backgroundColor: '#f5f5f5', // Light gray background
          color: '#212121', // Dark gray text
        },
        standardWarning: {
          backgroundColor: '#ffebee', // Lighter red
          color: '#c62828',
        },
        standardError: {
          backgroundColor: '#ffebee', // Lighter red
          color: '#c62828',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        colorPrimary: {
          backgroundColor: '#e0e0e0', // Light gray background
          color: '#212121', // Dark text
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': {
            color: '#212121',
          },
        },
      },
    },
  },
});

// Export both as default export and named export to support multiple import styles
export { theme }; // Named export
export default theme; // Default export